import React from "react";
import ReactDOM from "react-dom";
import { WidgetConfiguration, } from "@sg-widgets/shared-core";
import { WidgetConfigurationContext, } from "./configuration";
import { emit } from "./emit";
import debug from "./debug";
import { WebComponentErrorBoundary } from "./WebComponentErrorBoundary";
import { normalizePropsMapping, isPropToPropertyMappingWithAttribute, isPropToEventMapping, isPropToPropertySetterMapping, isPropToPropertyMapping, } from "./props";
import "./window-extensions";
import { mimickAsDocument } from "./utils";
import WithGlobalCss from "./components/WithGlobalCss";
export function widgetize(tagName, component, propMap, widgetOptions) {
    var _a;
    const normalizedPropMappings = normalizePropsMapping(tagName, propMap);
    if (window.__SG_WIDGETS_REACT_DESCRIPTORS__) {
        window.__SG_WIDGETS_REACT_DESCRIPTORS__[tagName] = {
            propMapping: normalizedPropMappings,
            api: widgetOptions?.exportReactAPI ?? {},
        };
    }
    const attributeToPropMapEntry = normalizedPropMappings.reduce((acc, x) => {
        if (isPropToPropertyMappingWithAttribute(x)) {
            acc[x.attribute.name] = x;
        }
        return acc;
    }, {});
    const widgetApi = widgetOptions?.exportReactAPI
        ? Object.keys(widgetOptions?.exportReactAPI).map((k) => [
            k,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            widgetOptions?.exportReactAPI[k],
        ])
        : [];
    const newClass = (_a = class SGWidget extends HTMLElement {
            constructor() {
                super();
                this.__privateState = {
                    props: {},
                    reactComponentRef: null,
                    renderRoot: undefined,
                    isConnected: false,
                };
                const props = this.__privateState.props;
                normalizedPropMappings.forEach((x) => {
                    if (isPropToEventMapping(x)) {
                        const eventName = x.event.name;
                        const eventOptions = x.event.options;
                        props[x.propName] = ((eventDetailData) => {
                            if (process.env.NODE_ENV !== "production")
                                debug("%s CustomElement emit event %s", tagName, eventName);
                            return emit(this, eventName, {
                                ...eventOptions,
                                detail: eventDetailData,
                            });
                        }); // eslint-disable-line @typescript-eslint/no-explicit-any
                    }
                    if (isPropToPropertySetterMapping(x)) {
                        const propNameToSet = x.setter.propName;
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        props[x.propName] = ((propValue) => {
                            if (process.env.NODE_ENV !== "production")
                                debug("%s CustomElement setting property %s", tagName, propNameToSet);
                            props[propNameToSet] = propValue;
                            setTimeout(() => this.__updateRender(), 0);
                        }); // eslint-disable-line @typescript-eslint/no-explicit-any
                    }
                    if (isPropToPropertyMapping(x)) {
                        /*
                        Here we make sure that properties were not already defined
                        on the customelement instance. If so we take their current
                        value, delete the property and set them again (which will
                        go through the property descriptors defined on the prototype).
              
                        This happens if the custom element in put in the DOM before
                        the script defining it is loaded. In this case we go through
                        the upgrade process:
                        https://html.spec.whatwg.org/multipage/custom-elements.html#custom-elements-upgrades-examples
                        */
                        if (Object.getOwnPropertyDescriptor(this, x.property.name)) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const thisAsAny = this;
                            const currentValue = thisAsAny[x.property.name];
                            delete thisAsAny[x.property.name];
                            thisAsAny[x.property.name] = currentValue;
                        }
                    }
                });
                if (widgetApi.length > 0) {
                    props.ref = (ref) => {
                        this.__privateState.reactComponentRef = ref;
                    };
                }
                if (process.env.NODE_ENV !== "production")
                    debug("%s CustomElement constructor", tagName);
            }
            get widgetConfiguration() {
                return SGWidget.__widgetConfiguration__;
            }
            connectedCallback() {
                if (process.env.NODE_ENV !== "production")
                    debug("%s CustomElement connectedCallback", tagName);
                this.__privateState.isConnected = true;
                this.__privateState.renderRoot = (this.widgetConfiguration.useShadowDOM
                    ? this.attachShadow({ mode: "open" })
                    : this).appendChild(document.createElement("span"));
                // Stop React from binding all events to the root document but to stay inside the custom element
                mimickAsDocument(this.__privateState.renderRoot);
                setTimeout(() => this.__updateRender(), 0);
            }
            disconnectedCallback() {
                if (process.env.NODE_ENV !== "production")
                    debug("%s CustomElement disconnectedCallback", tagName);
                this.__privateState.isConnected = false;
                // We will only get disconnected after connected so it is ok to assert.
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                ReactDOM.unmountComponentAtNode(this.__privateState.renderRoot);
            }
            adoptedCallback() {
                if (process.env.NODE_ENV !== "production")
                    debug("%s CustomElement adoptedCallback", tagName);
            }
            attributeChangedCallback(name, oldValue, newValue) {
                if (process.env.NODE_ENV !== "production")
                    debug("%s CustomElement attributeChangedCallback: %s oldValue %s newValue %s", tagName, name, oldValue, newValue);
                const attributeProp = attributeToPropMapEntry[name];
                if (attributeProp) {
                    // Here we set directly the property on the webcomponent (this will trigger a render)
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    this[attributeProp.property.name] =
                        newValue !== undefined
                            ? attributeProp.attribute.deserialize(newValue)
                            : undefined;
                }
            }
            /** @private */
            __updateRender() {
                // Early exit if we are not connected as we should not render in this case.
                if (!this.__privateState.isConnected)
                    return;
                if (process.env.NODE_ENV !== "production")
                    debug("%s CustomElement updateRender %O", tagName, this.__privateState.props);
                const Component = component;
                ReactDOM.render(<React.StrictMode>
          <WidgetConfigurationContext.Provider value={{ widgetConfiguration: this.widgetConfiguration }}>
            <WebComponentErrorBoundary>
              <WithGlobalCss>
                <Component {...this.__privateState.props}></Component>
              </WithGlobalCss>
            </WebComponentErrorBoundary>
          </WidgetConfigurationContext.Provider>
        </React.StrictMode>, 
                // rendering will only happen after we are connected so it is ok to assert
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                this.__privateState.renderRoot);
            }
        },
        _a.is = tagName,
        _a.observedAttributes = Object.keys(attributeToPropMapEntry),
        _a);
    const propertyDescriptors = normalizedPropMappings
        .filter(isPropToPropertyMapping)
        .reduce((descriptors, propMap) => {
        const propertyName = propMap.property.name;
        descriptors[propertyName] = {
            enumerable: true,
            configurable: false,
            get: function () {
                if (process.env.NODE_ENV !== "production")
                    debug("%s CustomElement getter %s", tagName, propertyName);
                return this.__privateState.props[propMap.propName];
            },
            set: function (value) {
                if (process.env.NODE_ENV !== "production")
                    debug("%s CustomElement setter %s", tagName, propertyName);
                this.__privateState.props[propMap.propName] = propMap.property.coerce(value); // eslint-disable-line @typescript-eslint/no-explicit-any
                setTimeout(() => this.__updateRender(), 0);
            },
        };
        return descriptors;
    }, {});
    if (widgetApi.length > 0) {
        for (const entry of widgetApi) {
            propertyDescriptors[entry[0]] = {
                configurable: false,
                enumerable: true,
                writable: false,
                value: function () {
                    const reactFn = this.__privateState.reactComponentRef?.[entry[0]];
                    if (process.env.NODE_ENV !== "production")
                        debug("%s CustomElement method %s", tagName, entry[0]);
                    if (typeof reactFn === "function") {
                        // eslint-disable-next-line @typescript-eslint/ban-types
                        return reactFn.apply(this.__privateState.reactComponentRef, 
                        // eslint-disable-next-line prefer-rest-params
                        arguments);
                    }
                    else {
                        this.widgetConfiguration.error(`React component is not initialized/rendered. Imperative API call ${entry[0]} is no-op`);
                    }
                },
            };
        }
    }
    Object.defineProperties(newClass.prototype, propertyDescriptors);
    newClass.__widgetConfiguration__ = new WidgetConfiguration(tagName, window.SGWTWidgetConfiguration, widgetOptions || {});
    customElements.define(tagName, newClass);
    return newClass;
}
